import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useModalControls } from 'src/js/hook/use-modal-controls';
import { useSuccessReaction } from 'src/js/hook/use-reaction';
import { useTask } from 'src/js/hook/use-task';
import { RebootResponse } from 'src/js/model/api/response/reboot-response';
import { SettingsTabContent } from 'src/js/pages/settings/settings-tab-content';
import { useListSelectionWithDataStore } from 'src/js/provider/list-selection-with-data-store-provider';
import { RebootNeededSavedModal } from 'src/js/reboot/reboot-needed-saved-modal';
import { Paths } from 'src/js/route';
import { createRebootTask } from 'src/js/task/settings-tasks';

import { LicenseDeleteModal } from './license-delete-modal';
import { LicenseFeatures } from './license-features';
import { LicenseImportModal } from './license-import-modal';
import { fetchLicenses } from './license-store';
import { LicenseTable } from './license-table';
import { LicenseViewModel } from './license-view-model';

export const Licenses: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // FIXME replace with <RebootNeeded> generic component
  const [rebootNeededSavedModalShown, showRebootNeededSavedModal, hideRebootNeededSavedModal] = useModalControls();

  const rebootTask = useTask(createRebootTask());
  const dataStore = useListSelectionWithDataStore<LicenseViewModel>();

  useSuccessReaction(rebootTask, (result: RebootResponse) => {
    navigate(result.upgrade ? Paths.rebootingUpgrade : Paths.rebooting);
  });

  const [loading, setLoading] = useState(true);
  const fetchData = () => {
    fetchLicenses().then((res: any) => {
      if (res.ok) {
        const response = res.data;
        if (response) {
          dataStore.setData(response.data);
        }
        setLoading(false);
      }
    });
  };

  // Load the data on mount
  useEffect(() => {
    fetchData();
  }, []);

  const shouldReloadData = (): void => {
    dataStore.unselectAll();
    fetchData();
  };

  const [deleteShown, showDelete, hideDelete] = useModalControls();
  const handleDelete = (failed: boolean): void => {
    hideDelete();
    shouldReloadData();
    if (!failed) {
      showRebootNeededSavedModal(); // FIXME add check for preset isSaved like on status page. Move to a common component.
    }
  };

  const [importShown, showImport, hideImport] = useModalControls();
  const handleImport = (): void => {
    hideImport();
    shouldReloadData();
    showRebootNeededSavedModal();
  };

  const handleOnReboot = (): void => {
    rebootTask();
  };

  return (
    <>
      <SettingsTabContent name={t('settings.licensing.title')}>
        <LicenseTable onDelete={showDelete} showImport={showImport} loading={loading} />
        <LicenseFeatures />
      </SettingsTabContent>

      {deleteShown && (
        <LicenseDeleteModal
          licensesToDelete={dataStore.selectedRows}
          onCancel={() => {
            hideDelete();
            dataStore.unselectAll();
          }}
          onApply={handleDelete}
        />
      )}
      {importShown && <LicenseImportModal onCancel={hideImport} onImport={handleImport} />}
      {rebootNeededSavedModalShown && (
        <RebootNeededSavedModal
          onCancel={hideRebootNeededSavedModal}
          onReboot={() => {
            hideRebootNeededSavedModal();
            handleOnReboot();
          }}
        />
      )}
    </>
  );
};
